import {
  formatName,
  formatPrice,
  formatRelativeDate,
  formatTime,
  IconTest,
  isDateAfter,
  pluralize,
  ProfileImage,
  Rating,
  RatingSize,
  Tag,
} from '@leland-dev/leland-ui-library';
import { type FC, type PropsWithChildren, useMemo } from 'react';

import {
  ExperienceType,
  GoalName,
  type Scalars,
  SchoolAdmissionStatus,
} from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { categoryUrnToSlug } from '../../utils/category';
import { getCoachUrl } from '../../utils/coach';
import useMediaQuery, { Breakpoints } from '../../utils/hooks/useMediaQuery';
import { TierBadge } from '../TierBadge';
import AdditionalExperienceTag from '../coach/AdditionalExperienceTag';
import CompanyTag from '../coach/CompanyTag';
import SchoolTag from '../coach/SchoolTag';
import { type OrganizationTagDetails } from '../common/OrganizationTag';

import { CoachCardPackageCarousel } from './CoachCardPackageCarousel';
import {
  type CoachCardCoachFragment,
  type CoachCardOfferingPackageFragment,
  type CoachCardSkuFragment,
} from './__generated-gql-types__/CoachCard.generated';

type Coach = CoachCardCoachFragment & {
  searchPackages?: {
    total: number;
    results: CoachCardOfferingPackageFragment[];
  };
};

interface CoachCardProps {
  coach: Coach;
  categoryId: Possible<Scalars['CategoryUrn']['output']>;
  selectedSchoolIds: Possible<Array<Scalars['SchoolUrn']['output']>>;
  selectedCompanyIds: Possible<Array<Scalars['CompanyUrn']['output']>>;
  className?: string;
  onClick?: () => void;
  hidePrice: boolean;
  skusData: Possible<CoachCardSkuFragment[]>;
}

const CoachCard: React.FC<CoachCardProps> = ({
  coach,
  categoryId,
  selectedSchoolIds,
  selectedCompanyIds,
  className = '',
  onClick,
  hidePrice: hidePriceProp,
  skusData,
}) => {
  const isTablet = useMediaQuery(Breakpoints.TAB);

  const { currentUser, isNewSubscriptionExperience } = useAuth();

  const shouldShowCoachTier = false;

  const showStartingPrice =
    (isNewSubscriptionExperience || coach.newExperience) && currentUser;

  const coachStartingPrice = useMemo(
    () => skusData?.find((sku) => sku.tier === coach.tier)?.unitPrice ?? null,
    [skusData, coach.tier],
  );

  const hidePrice = hidePriceProp || showStartingPrice;

  const formattedCoachName = formatName({
    firstName: coach.user.firstName,
    lastName: coach.user.lastName,
  });

  const hasEducationAdmissionsCommitteeExperience =
    coach.educationExperiences?.some(
      (education) => education.admissionCommittee,
    );
  const hasProfessionalAdmissionsCommitteeExperience =
    coach.professionalExperiences?.some(
      (experience) =>
        experience.experienceType === ExperienceType.ADMISSIONS_COMMITTEE,
    );
  const hasProfessionalHiringExperience = coach.professionalExperiences?.some(
    (experience) => experience.experienceType === ExperienceType.HIRING_MANAGER,
  );

  const coachEducationExperiences = coach.educationExperiences;
  const schoolsAdmittedTo = useMemo(
    () =>
      (coach.schoolAdmissionInfoList ?? []).filter(
        (schoolAdmissionInfo) =>
          schoolAdmissionInfo?.status === SchoolAdmissionStatus.ADMITTED,
      ),
    [coach.schoolAdmissionInfoList],
  );
  const schoolTagDetails: Nullable<OrganizationTagDetails> = useMemo(() => {
    const defaultSchool = coachEducationExperiences?.[0]?.school;
    const studiedAt = selectedSchoolIds
      ? coachEducationExperiences?.find((education) =>
          selectedSchoolIds.includes(education.school.id),
        )?.school
      : null;
    const admittedTo = selectedSchoolIds
      ? schoolsAdmittedTo.find((s) => selectedSchoolIds.includes(s.school.id))
          ?.school
      : null;
    const featuredSchool = coachEducationExperiences?.find(
      (education) => education.isFeatured,
    )?.school;

    const school = studiedAt ?? admittedTo ?? featuredSchool ?? defaultSchool;

    const baseStudiedAtText = isTablet ? 'Studied at ' : '';
    const baseAdmittedToText = isTablet ? 'Admitted to ' : '';
    const text = studiedAt
      ? `${baseStudiedAtText}${studiedAt.name}`
      : admittedTo
        ? `${baseAdmittedToText}${admittedTo.name}`
        : (featuredSchool ?? defaultSchool)
          ? `${baseStudiedAtText}${(featuredSchool ?? defaultSchool)?.name}`
          : '';

    return school
      ? {
          organization: {
            name: school.name,
            logoLink: school.logoLink,
          },
          text,
        }
      : null;
  }, [
    selectedSchoolIds,
    coachEducationExperiences,
    schoolsAdmittedTo,
    isTablet,
  ]);

  const coachProfessionalExperiences = coach.professionalExperiences;
  const companiesAdmitted = useMemo(
    () => coach.companiesAdmitted ?? [],
    [coach.companiesAdmitted],
  );
  const companyTagDetails: Nullable<OrganizationTagDetails> = useMemo(() => {
    const defaultCompany = coachProfessionalExperiences?.[0];
    const worksAt = selectedCompanyIds
      ? coachProfessionalExperiences?.find((experience) =>
          selectedCompanyIds.includes(experience.company.id ?? ''),
        )
      : null;
    const gotInto = selectedCompanyIds
      ? companiesAdmitted.find((company) =>
          selectedCompanyIds.includes(company.id ?? ''),
        )
      : null;
    const featuredCompany = coachProfessionalExperiences?.find(
      (company) => company.isFeatured,
    );

    const company =
      worksAt?.company ??
      gotInto ??
      featuredCompany?.company ??
      defaultCompany?.company;

    const baseWorksdAtText = isTablet
      ? `Work${
          (worksAt ?? featuredCompany ?? defaultCompany)?.endAt?.year
            ? 'ed'
            : 's'
        } at `
      : '';
    const baseGotIntoText = isTablet ? 'Got into ' : '';
    const text = worksAt?.company
      ? `${baseWorksdAtText}${worksAt.company.name}`
      : gotInto
        ? `${baseGotIntoText}${gotInto.name}`
        : (featuredCompany ?? defaultCompany)
          ? `${baseWorksdAtText}${
              (featuredCompany ?? defaultCompany)?.company.name
            }`
          : '';

    return company
      ? {
          organization: {
            name: company.name,
            logoLink: company.logoLink,
          },
          text,
        }
      : null;
  }, [
    selectedCompanyIds,
    coachProfessionalExperiences,
    companiesAdmitted,
    isTablet,
  ]);

  const searchedCategoryInfo = coach.categoryInformationList?.find(
    // if no categoryId, return first result
    // otherwise find matching info
    (catInfo) => !categoryId || catInfo.category.id === categoryId,
  );

  const nextAvailability = coach.availability?.nextAvailableAt;

  const HOURLY_AND_PACKAGES_INFO = (
    <div className="mt-3 flex flex-col items-start justify-center md:mt-0 md:items-end">
      {!hidePrice && coach.hourlyRate ? (
        <p className="text-lg font-medium text-leland-gray-dark">
          {formatPrice(coach.hourlyRate)}
          <span className="text-base font-normal text-leland-gray-light">
            /hr
          </span>
        </p>
      ) : null}
      {showStartingPrice && coachStartingPrice ? (
        <p className="text-base text-leland-gray-light">
          Starting at{' '}
          <span className="text-leland-gray-dark">
            {formatPrice(coachStartingPrice)}
          </span>
          /hr
        </p>
      ) : null}
      {nextAvailability && isDateAfter(nextAvailability, Date.now()) ? (
        <p className="whitespace-nowrap text-sm font-medium text-leland-primary">
          Available {formatRelativeDate(nextAvailability, Date.now())} at{' '}
          {formatTime(nextAvailability, { excludeTimeZone: true })}
        </p>
      ) : null}
    </div>
  );

  const href = getCoachUrl({
    coachSlug: coach.slug,
    categorySlug: searchedCategoryInfo
      ? categoryUrnToSlug(searchedCategoryInfo.category.slug)
      : undefined,
  });

  return (
    <div
      className={`overflow-hidden py-6 lg:px-6 lg:hover:bg-gray-50 ${className}`}
    >
      <CardLink
        className="flex items-start justify-center"
        href={href}
        onClick={onClick}
      >
        <ProfileImage
          className="hidden h-25 w-25 shrink-0 md:flex"
          pictureLink={coach.user.pictureLink}
          firstName={coach.user.firstName}
          lastName={coach.user.lastName}
        />
        <div className="flex grow flex-col items-start justify-start overflow-hidden md:ml-7">
          <div className="flex w-full items-center justify-start font-medium md:items-start md:justify-between">
            <ProfileImage
              className="mr-4 size-16 shrink-0 md:hidden"
              pictureLink={coach.user.pictureLink}
              firstName={coach.user.firstName}
              lastName={coach.user.lastName}
            />
            <div className="flex flex-col items-start justify-center">
              <div className="flex items-center justify-start space-x-2">
                <p className="shrink-0 text-base text-leland-gray-light">
                  {formattedCoachName}
                </p>
                {coach.reviewTotal ? (
                  <div className="line-clamp-1 flex items-center text-base font-normal text-leland-gray-light">
                    <div className="mx-2 size-0.5 rounded-full bg-leland-gray-light" />
                    <Rating
                      totalStars={1}
                      rate={coach.averageReviewRate}
                      showRate
                      size={RatingSize.SMALL}
                    />
                    <p className="ml-1 line-clamp-1">
                      ({pluralize(coach.reviewTotal, 'Review')})
                    </p>
                  </div>
                ) : null}

                {shouldShowCoachTier ? (
                  <>
                    {/* only show divider if rating is also shown */}
                    {coach.reviewTotal ? (
                      <div className="size-0.5 rounded bg-leland-gray-light" />
                    ) : null}
                    <TierBadge
                      tier={coach.tier}
                      goal={searchedCategoryInfo?.category.goal}
                    />
                  </>
                ) : null}
              </div>
              {searchedCategoryInfo?.headline ? (
                <h2 className="line-clamp-2 break-words text-lg leading-6 text-leland-gray-dark">
                  {searchedCategoryInfo?.headline}
                </h2>
              ) : null}
            </div>
            {isTablet ? HOURLY_AND_PACKAGES_INFO : null}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-start gap-2 md:mt-2">
            {schoolTagDetails?.organization ? (
              <SchoolTag
                key={schoolTagDetails.organization.name}
                selectedSchoolTagDetails={schoolTagDetails}
              />
            ) : null}
            {companyTagDetails?.organization ? (
              <CompanyTag
                key={companyTagDetails.organization.name}
                selectedCompanyTagDetails={companyTagDetails}
              />
            ) : null}
            {searchedCategoryInfo?.testScore ? (
              <Tag
                key={searchedCategoryInfo.category.id}
                LeftIcon={IconTest}
                text={`${searchedCategoryInfo.testScore} on the ${searchedCategoryInfo.category.name}`}
              />
            ) : null}
            {searchedCategoryInfo?.category.goal === GoalName.SCHOOL &&
            (hasEducationAdmissionsCommitteeExperience ||
              hasProfessionalAdmissionsCommitteeExperience) ? (
              <AdditionalExperienceTag
                type={ExperienceType.ADMISSIONS_COMMITTEE}
              />
            ) : null}
            {searchedCategoryInfo?.category.goal === GoalName.CAREER &&
            hasProfessionalHiringExperience ? (
              <AdditionalExperienceTag type={ExperienceType.HIRING_MANAGER} />
            ) : null}
          </div>
          {(searchedCategoryInfo?.qualifications ?? coach.bio) ? (
            <div className="mt-4 line-clamp-2 break-words text-leland-gray-light md:mt-3">
              {searchedCategoryInfo?.qualifications ?? coach.bio}
            </div>
          ) : null}
          {!isTablet ? HOURLY_AND_PACKAGES_INFO : null}
        </div>
      </CardLink>
      {coach.searchPackages?.results.length ? (
        <div className="flex overflow-hidden">
          {/* clickable placeholder */}
          <CardLink
            className="hidden w-36 md:block"
            href={href}
            onClick={onClick}
          />
          <CoachCardPackageCarousel
            className="mt-5"
            packages={coach.searchPackages.results}
            coachSlug={coach.slug}
          />
        </div>
      ) : null}
    </div>
  );
};

type CardLinkProp = PropsWithChildren<{
  href: string;
  onClick?: () => void;
  className: string;
}>;

const CardLink: FC<CardLinkProp> = ({ href, onClick, className, children }) => {
  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
    >
      {children}
    </a>
  );
};

export default CoachCard;
